// ChargemasterEditionGrid — the AHP edition of a hospital's price file as a
// spreadsheet: every deduplicated line we imported, with our enrichment
// (plain-language name, U.S. median, vs-median %, percentile, flags), sortable,
// filterable, virtualized for 100k+ rows, with the XLSX / CSV / report PDF
// downloads and the original file's first lines alongside.
//
// Data: scripts/generate_hospital_edition.py → edition.json + edition-rows.json
// (see src/hooks/useHospitalEdition.ts). Grid: TanStack Table (headless) +
// TanStack Virtual, so it takes the civic theme and stays MIT-licensed.
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, FileSpreadsheet, FileText, Search, X } from "lucide-react";
import {
  type ColumnDef, type ColumnFiltersState, type SortingState, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { titleCaseHospital } from "@/lib/hospitalName";
import type { EditionManifest, EditionRows, NearbyPick } from "@/hooks/useHospitalEdition";

type Cell = string | number | null | NearbyPick;
type Row = Cell[];
const NEARBY_COLS = ["nb30", "nb60", "nb120"] as const;
const isPick = (v: Cell): v is NearbyPick => Array.isArray(v) && v.length === 3;

/** The site's canonical code page, mirroring codeHrefForHospital() in functions/lib/pages/hospital.ts. */
function codeHref(family: Cell, code: Cell): string | null {
  const c = code == null ? "" : String(code);
  if (!c) return null;
  switch (family) {
    case "CPT": return `/cpt/${c}`;
    case "HCPCS": return `/hcpcs/${c}`;
    case "REV": return `/rev/${c}`;
    case "NDC": return `/ndc/${c}`;
    case "MS-DRG": { const d = c.toUpperCase(); if (d.startsWith("MS-")) return `/drg/${d}`; if (/^\d+$/.test(d)) return `/drg/MS-${(d.replace(/^0+/, "") || "0").padStart(3, "0")}`; return `/drg/${c}`; }
    default: return null;
  }
}

export interface ChargemasterEditionGridProps {
  manifest: EditionManifest;
  data: EditionRows;
  head: string | null;
  hospitalName: string;
  isDark?: boolean;
  /** Extra toolbar buttons (e.g. "Open original file"). */
  actions?: React.ReactNode;
  heightClass?: string;
}

const ROW_H = 32;
const fmtUSD = (v: Cell) => (v == null || v === "" || !Number.isFinite(Number(v)) ? "" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtPct = (v: Cell) => (v == null || v === "" || !Number.isFinite(Number(v)) ? "" : `${Number(v) >= 0 ? "+" : "−"}${Math.abs(Math.round(Number(v)))}%`);
const fmtInt = (v: Cell) => (v == null || v === "" ? "" : Number(v).toLocaleString("en-US"));
const colLetter = (i: number) => { let s = ""; let n = i; do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0); return s; };

const MONEY = new Set(["gross_charge", "cash_price", "min_negotiated", "max_negotiated", "us_median"]);
const RIGHT = new Set([...MONEY, "vs_us_median_pct", "us_percentile", "raw_rows", "unit_quantity", "line"]);
const LABEL_OVERRIDE: Record<string, string> = { nb30: "Cheaper within 30 mi", nb60: "Cheaper within 60 mi", nb120: "Cheaper within 120 mi" };
const WIDTH: Record<string, number> = {
  line: 64, family: 84, code: 92, description: 360, consumer_name: 260, local_code: 120, setting: 100, unit: 80, unit_quantity: 84,
  gross_charge: 130, cash_price: 130, min_negotiated: 130, max_negotiated: 130, us_median: 130, vs_us_median_pct: 100, us_percentile: 96, raw_rows: 84, flags: 200, error_reason: 260,
  nb30: 300, nb60: 300, nb120: 300,
};

export function ChargemasterEditionGrid({ manifest, data, head, hospitalName, isDark = false, actions, heightClass = "h-[620px]" }: ChargemasterEditionGridProps) {
  const { t } = useTranslation(["hospital"]);
  const tr = (key: string, fallback: string, opts?: Record<string, unknown>) => t(`hospital:editionGrid.${key}`, { defaultValue: fallback, ...(opts || {}) });

  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [showHead, setShowHead] = useState(false);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [nearbyOnly, setNearbyOnly] = useState(false);
  const hospitals = data.hospitals || [];
  const nearbyAvailable = NEARBY_COLS.some((c) => data.cols.includes(c)) && hospitals.length > 0;

  const idx = useMemo(() => Object.fromEntries(data.cols.map((c, i) => [c, i])) as Record<string, number>, [data.cols]);
  // Drop columns this file never fills (a CSV with no cash prices shouldn't show an empty column).
  const visibleCols = useMemo(() => data.cols.filter((c) => {
    if (c === "line" || c === "code" || c === "description" || c === "gross_charge" || c === "family") return true;
    const i = idx[c];
    return data.rows.some((r) => r[i] != null && r[i] !== "");
  }), [data, idx]);
  const families = useMemo(() => Object.entries(manifest.counts.families || {}).sort((a, b) => b[1] - a[1]), [manifest]);
  const settings = useMemo(() => { const i = idx.setting; if (i == null) return [] as string[]; const s = new Set<string>(); for (const r of data.rows) if (r[i]) s.add(String(r[i])); return [...s].sort(); }, [data, idx]);

  const columns = useMemo<ColumnDef<Row, Cell>[]>(() => visibleCols.map((c) => ({
    id: c,
    // nearby cells sort by the alternative's price; empty cells are `undefined`
    // (not null) so TanStack's sortUndefined:"last" actually applies to them
    accessorFn: (row) => { const v = row[idx[c]]; if ((NEARBY_COLS as readonly string[]).includes(c)) return isPick(v) ? v[1] : undefined; return v == null || v === "" ? undefined : v; },
    header: LABEL_OVERRIDE[c] || manifest.column_labels[c] || c,
    size: WIDTH[c] || 120,
    enableGlobalFilter: ["code", "description", "consumer_name", "local_code", "flags", "error_reason"].includes(c),
    filterFn: (row, id, value) => { const v = row.getValue<Cell | undefined>(id); return value == null || value === "" ? true : id === "flags" ? (value === "__any" ? !!v : String(v || "") === value) : id === "nb120" ? (value === "__any" ? v != null : true) : String(v ?? "") === String(value); },
    sortingFn: MONEY.has(c) || RIGHT.has(c) || (NEARBY_COLS as readonly string[]).includes(c) ? "basic" : "alphanumeric",
    // Numbers open on "most expensive first" — that is the question readers bring.
    sortDescFirst: MONEY.has(c) || c === "vs_us_median_pct" || c === "us_percentile" || c === "raw_rows",
    sortUndefined: "last",
    cell: (info) => {
      const v = info.getValue();
      if ((NEARBY_COLS as readonly string[]).includes(c)) {
        const pick = info.row.original[idx[c]];
        if (!isPick(pick)) return "";
        const h = hospitals[pick[0]];
        if (!h) return fmtUSD(pick[1]);
        const gross = Number(info.row.original[idx.gross_charge]);
        const save = gross > 0 ? Math.round((1 - pick[1] / gross) * 100) : null;
        return (
          <a href={`/hospital/${h[0]}`} target="_blank" rel="noopener" title={`${titleCaseHospital(h[1]) || h[1]}, ${titleCaseHospital(h[2]) || h[2]}, ${h[3]} — ${pick[2].toFixed(0)} straight-line miles`} style={{ color: isDark ? "#86efac" : "#0F6B49", textDecoration: "underline", textUnderlineOffset: 2 }}>
            <span style={{ fontWeight: 700 }}>{fmtUSD(pick[1])}</span>{save != null && save > 0 ? ` (−${save}%)` : ""} · {titleCaseHospital(h[1]) || h[1]} · {pick[2].toFixed(0)} mi
          </a>
        );
      }
      if (c === "code") {
        const href = codeHref(info.row.original[idx.family], v);
        return href ? <a href={href} target="_blank" rel="noopener" title={tr("codeLink", "Open our U.S. breakdown of this code")} style={{ color: isDark ? "#C5A55A" : "#002868", textDecoration: "underline", textUnderlineOffset: 2 }}>{String(v)}</a> : String(v ?? "");
      }
      if (MONEY.has(c)) return fmtUSD(v);
      if (c === "vs_us_median_pct") { const n = Number(v); const s = fmtPct(v); return s ? <span style={{ color: n >= 50 ? (isDark ? "#E4566A" : "#B22234") : n <= -10 ? (isDark ? "#86efac" : "#0F6B49") : undefined, fontWeight: 700 }}>{s}</span> : ""; }
      if (c === "us_percentile" || c === "raw_rows") return fmtInt(v);
      if (c === "line") return <span style={{ opacity: 0.6 }}>{fmtInt(v)}</span>;
      return v == null ? "" : String(v);
    },
  })), [visibleCols, idx, manifest.column_labels, isDark, hospitals]);

  const table = useReactTable({
    data: data.rows, columns, state: { globalFilter, sorting, columnFilters },
    onGlobalFilterChange: setGlobalFilter, onSortingChange: setSorting, onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
  });
  useEffect(() => { table.getColumn("flags")?.setFilterValue(flaggedOnly ? "__any" : ""); }, [flaggedOnly, table]);
  useEffect(() => { table.getColumn("nb120")?.setFilterValue(nearbyOnly ? "__any" : ""); }, [nearbyOnly, table]);

  const rows = table.getRowModel().rows;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({ count: rows.length, getScrollElement: () => scrollRef.current, estimateSize: () => ROW_H, overscan: 14 });
  const vItems = virtualizer.getVirtualItems();
  const padTop = vItems.length ? vItems[0].start : 0;
  const padBottom = vItems.length ? virtualizer.getTotalSize() - vItems[vItems.length - 1].end : 0;

  const P = isDark
    ? { sheetBg: "#0b1630", grid: "rgba(255,255,255,0.10)", headBg: "#122246", headInk: "rgba(240,235,227,0.88)", gutterBg: "#10203f", gutterInk: "rgba(240,235,227,0.55)", ink: "#F0EBE3", zebra: "rgba(255,255,255,0.025)", letterBg: "#0e1c3a", chipOn: "#C5A55A", chipOnInk: "#1B1B1B", chipOff: "rgba(240,235,227,0.06)", muted: "rgba(240,235,227,0.62)" }
    : { sheetBg: "#FFFFFF", grid: "#D9DEE7", headBg: "#EEF2F8", headInk: "#1F2A44", gutterBg: "#F3F5F9", gutterInk: "#6B7280", ink: "#111827", zebra: "#FAFBFD", letterBg: "#E7ECF4", chipOn: "#002868", chipOnInk: "#FAF9F6", chipOff: "rgba(27,27,27,0.05)", muted: "#5B6472" };
  const mono = "ui-monospace, SFMono-Regular, Menlo, monospace";
  const chip = (on: boolean): React.CSSProperties => ({ padding: "4px 10px", borderRadius: 999, border: `1px solid ${on ? "transparent" : P.grid}`, background: on ? P.chipOn : P.chipOff, color: on ? P.chipOnInk : P.ink, fontSize: 12, fontWeight: 700, cursor: "pointer" });
  const familyFilter = (table.getColumn("family")?.getFilterValue() as string) || "";
  const settingFilter = (table.getColumn("setting")?.getFilterValue() as string) || "";
  const total = data.rows.length;
  const gen = manifest.generated_at ? new Date(manifest.generated_at) : null;

  return (
    <div className="flex flex-col h-full min-h-0" style={{ color: P.ink }}>
      {/* Stamp + downloads */}
      <div className="flex flex-wrap items-start gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate" title={manifest.file.name || undefined}>
            {tr("title", "AHP edition · {{name}}", { name: manifest.file.name || hospitalName })}
          </div>
          <div className="text-xs" style={{ color: P.muted }}>
            {tr("stamp", "{{lines}} deduplicated lines from {{raw}} imported rows · {{year}} file · generated {{date}}", {
              lines: total.toLocaleString(), raw: manifest.counts.raw_rows.toLocaleString(), year: manifest.year, date: gen ? gen.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—",
            })}
            {manifest.counts.flagged > 0 && ` · ${tr("flaggedCount", "{{n}} flagged", { n: manifest.counts.flagged.toLocaleString() })}`}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" size="sm"><a href={manifest.files.xlsx} target="_blank" rel="noopener"><FileSpreadsheet className="w-4 h-4 mr-2" />{tr("xlsx", "XLSX")}</a></Button>
          <Button asChild variant="outline" size="sm"><a href={manifest.files.csv} target="_blank" rel="noopener"><Download className="w-4 h-4 mr-2" />{tr("csv", "CSV")}</a></Button>
          {manifest.files.report_pdf && <Button asChild variant="outline" size="sm"><a href={manifest.files.report_pdf} target="_blank" rel="noopener"><FileText className="w-4 h-4 mr-2" />{tr("report", "Report PDF")}</a></Button>}
          {manifest.head.available && (
            <Button variant={showHead ? "default" : "outline"} size="sm" onClick={() => setShowHead((v) => !v)} aria-pressed={showHead}>{showHead ? tr("hideHead", "Hide original") : tr("showHead", "Original as published")}</Button>
          )}
          {actions}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="relative w-72 max-w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: P.muted }} />
          <Input value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder={tr("search", "Find a code, description or name…")} className="pl-9 h-9" aria-label={tr("search", "Find a code, description or name…")} />
          {globalFilter && <button type="button" onClick={() => setGlobalFilter("")} className="absolute right-2 top-1/2 -translate-y-1/2" aria-label={tr("clear", "Clear")}><X className="w-4 h-4" style={{ color: P.muted }} /></button>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button type="button" style={chip(!familyFilter)} onClick={() => table.getColumn("family")?.setFilterValue("")} aria-pressed={!familyFilter}>{tr("allTypes", "All types")} · {total.toLocaleString()}</button>
          {families.map(([f, n]) => (
            <button key={f} type="button" style={chip(familyFilter === f)} onClick={() => table.getColumn("family")?.setFilterValue(familyFilter === f ? "" : f)} aria-pressed={familyFilter === f}>{f} · {n.toLocaleString()}</button>
          ))}
        </div>
        {settings.length > 1 && (
          <select value={settingFilter} onChange={(e) => table.getColumn("setting")?.setFilterValue(e.target.value)} className="h-8 rounded-md border px-2 text-xs" style={{ borderColor: P.grid, background: "transparent", color: P.ink }} aria-label={tr("setting", "Setting")}>
            <option value="">{tr("anySetting", "Any setting")}</option>
            {settings.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {manifest.counts.flagged > 0 && idx.flags != null && (
          <button type="button" style={chip(flaggedOnly)} onClick={() => setFlaggedOnly((v) => !v)} aria-pressed={flaggedOnly}>{tr("flaggedOnly", "Flagged only")}</button>
        )}
        {nearbyAvailable && (manifest.counts.nearby?.["120"] ?? 0) > 0 && (
          <button type="button" style={chip(nearbyOnly)} onClick={() => setNearbyOnly((v) => !v)} aria-pressed={nearbyOnly}>{tr("nearbyOnly", "Cheaper nearby · {{n}}", { n: (manifest.counts.nearby?.["120"] ?? 0).toLocaleString() })}</button>
        )}
        <span className="ml-auto text-xs" style={{ color: P.muted }}>{tr("showing", "{{n}} of {{total}} lines", { n: rows.length.toLocaleString(), total: total.toLocaleString() })}</span>
      </div>

      <div className={`grid gap-3 min-h-0 ${showHead ? "lg:grid-cols-[3fr_2fr]" : ""} ${heightClass}`}>
        {/* The sheet */}
        <div ref={scrollRef} className="min-h-0 h-full overflow-auto rounded-md border" style={{ borderColor: P.grid, background: P.sheetBg }}>
          <table className="border-collapse text-[12.5px]" style={{ fontFamily: mono, minWidth: "100%", tableLayout: "fixed", width: visibleCols.reduce((w, c) => w + (WIDTH[c] || 120), 56) }}>
            <thead className="sticky top-0 z-10">
              <tr style={{ background: P.letterBg, color: P.gutterInk, fontSize: 10.5 }}>
                <th className="sticky left-0 z-20" style={{ background: P.letterBg, width: 56, minWidth: 56, borderRight: `1px solid ${P.grid}`, borderBottom: `1px solid ${P.grid}`, padding: "2px 4px" }} />
                {visibleCols.map((c, i) => <th key={c} className="font-normal text-center" style={{ width: WIDTH[c] || 120, borderRight: `1px solid ${P.grid}`, borderBottom: `1px solid ${P.grid}`, padding: "2px 6px" }}>{colLetter(i)}</th>)}
              </tr>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} style={{ background: P.headBg, color: P.headInk }}>
                  <th className="sticky left-0 z-20 text-center font-semibold" style={{ background: P.headBg, color: P.gutterInk, borderRight: `1px solid ${P.grid}`, borderBottom: `1px solid ${P.grid}`, padding: "6px 4px" }}>1</th>
                  {hg.headers.map((h) => {
                    const c = h.column.id; const sorted = h.column.getIsSorted();
                    return (
                      <th key={h.id} className="font-semibold select-none cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis" title={String(h.column.columnDef.header)} onClick={h.column.getToggleSortingHandler()}
                        style={{ width: WIDTH[c] || 120, textAlign: RIGHT.has(c) ? "right" : "left", borderRight: `1px solid ${P.grid}`, borderBottom: `1px solid ${P.grid}`, padding: "6px 8px" }}>
                        <span className="inline-flex items-center gap-1 max-w-full">
                          <span className="truncate">{flexRender(h.column.columnDef.header, h.getContext())}</span>
                          {sorted === "asc" ? <ArrowUp className="w-3 h-3 shrink-0" /> : sorted === "desc" ? <ArrowDown className="w-3 h-3 shrink-0" /> : <ArrowUpDown className="w-3 h-3 shrink-0 opacity-30" />}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {padTop > 0 && <tr style={{ height: padTop }}><td colSpan={visibleCols.length + 1} /></tr>}
              {vItems.map((vi) => {
                const row = rows[vi.index];
                return (
                  <tr key={row.id} style={{ height: ROW_H, background: vi.index % 2 ? P.zebra : "transparent" }}>
                    <td className="sticky left-0 z-10 text-center" style={{ background: P.gutterBg, color: P.gutterInk, borderRight: `1px solid ${P.grid}`, borderBottom: `1px solid ${P.grid}`, padding: "0 4px", fontSize: 11 }}>{vi.index + 2}</td>
                    {row.getVisibleCells().map((cell) => {
                      const c = cell.column.id; const raw = cell.getValue<Cell>();
                      return (
                        <td key={cell.id} title={raw == null || (NEARBY_COLS as readonly string[]).includes(c) ? undefined : String(raw)} style={{ width: WIDTH[c] || 120, textAlign: RIGHT.has(c) ? "right" : "left", borderRight: `1px solid ${P.grid}`, borderBottom: `1px solid ${P.grid}`, padding: "0 8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {padBottom > 0 && <tr style={{ height: padBottom }}><td colSpan={visibleCols.length + 1} /></tr>}
              {rows.length === 0 && <tr><td colSpan={visibleCols.length + 1} className="text-center" style={{ padding: 24, color: P.muted }}>{tr("noMatch", "No line matches these filters.")}</td></tr>}
            </tbody>
          </table>
        </div>

        {/* The original, as published */}
        {showHead && (
          <div className="min-h-0 h-full overflow-auto rounded-md border" style={{ borderColor: P.grid, background: P.sheetBg }}>
            <div className="sticky top-0 z-10 px-3 py-2 text-xs font-semibold" style={{ background: P.headBg, color: P.headInk, borderBottom: `1px solid ${P.grid}` }}>
              {tr("headTitle", "The original file, as published")} <span className="font-normal" style={{ color: P.muted }}>· {manifest.head.note}</span>
            </div>
            <pre className="text-[11px] leading-[18px] m-0 p-3" style={{ fontFamily: mono, color: P.ink, whiteSpace: "pre", tabSize: 12 }}>
              {(head || "").split("\n").map((l, i) => <div key={i}><span style={{ display: "inline-block", width: 34, color: P.gutterInk, userSelect: "none" }}>{i + 1}</span>{l}</div>)}
            </pre>
          </div>
        )}
      </div>

      <p className="mt-2 text-[11px]" style={{ color: P.muted }}>
        {tr("foot", "One line per code, description, setting and unit; payer duplicates collapsed, gross charge = the highest across them, with the original row count in 'Rows in original'. U.S. median and percentile are ours, from the hospitals whose files we hold. Plain-language names are ours, not the AMA's CPT® descriptors; CPT® is a registered trademark of the American Medical Association.")}
        {nearbyAvailable && ` ${tr("nearbyFoot", "'Cheaper within N mi' = the hospital with the lowest published price for the same code inside that straight-line radius, when it is at least 10% under this line; bands are cumulative. It is that hospital's own published charge — a starting point for a call, not a quote.")}`}
        {manifest.position && ` ${tr("position", "Price position: {{p}}.", { p: manifest.position })}`}
      </p>
    </div>
  );
}
